import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getCompletePlayerStatistics } from '../services/statistics';
import { PlayerStatistics } from '../types';
import WinRateChart from '../components/statistics/WinRateChart';
import FrameWinRateChart from '../components/statistics/FrameWinRateChart';
import BreakDistributionChart from '../components/statistics/BreakDistributionChart';
import './Statistics.css';

const Statistics: React.FC = () => {
  const { t } = useTranslation();
  const { player } = useAuth();
  const [statistics, setStatistics] = useState<PlayerStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStatistics();
  }, [player]);

  const loadStatistics = async () => {
    if (!player?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const stats = await getCompletePlayerStatistics(player.id);
      setStatistics(stats);
    } catch (err) {
      console.error('Error loading statistics:', err);
      setError(t('statistics.error.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="statistics-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="statistics-page">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={loadStatistics}>{t('common.retry')}</button>
        </div>
      </div>
    );
  }

  if (!statistics) {
    return (
      <div className="statistics-page">
        <div className="no-data-message">
          <h2>{t('statistics.noData.title')}</h2>
          <p>{t('statistics.noData.message')}</p>
        </div>
      </div>
    );
  }

  const { games, breaks, scoring, ballPotting, breakDistribution, recentForm } = statistics;

  return (
    <div className="statistics-page">
      <header className="statistics-header">
        <h1>{t('statistics.title')}</h1>
        <p className="statistics-subtitle">{t('statistics.subtitle')}</p>
      </header>

      {/* Overview Cards */}
      <section className="stats-overview">
        <h2>{t('statistics.overview.title')}</h2>
        
        <div className="stats-grid">
          {/* Game Stats Card */}
          <div className="stat-card">
            <div className="stat-card-header">
              <span className="stat-icon">🎮</span>
              <h3>{t('statistics.games.title')}</h3>
            </div>
            <div className="stat-card-body">
              <div className="stat-item-large">
                <span className="stat-value">{games.totalGames}</span>
                <span className="stat-label">{t('statistics.games.totalGames')}</span>
              </div>
              <div className="stat-row">
                <div className="stat-item">
                  <span className="stat-value stat-positive">{games.gamesWon}</span>
                  <span className="stat-label">{t('statistics.games.won')}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value stat-negative">{games.gamesLost}</span>
                  <span className="stat-label">{t('statistics.games.lost')}</span>
                </div>
              </div>
              <div className="stat-item-highlight">
                <span className="stat-value-big">{games.winRate.toFixed(1)}%</span>
                <span className="stat-label">{t('statistics.games.winRate')}</span>
              </div>
            </div>
          </div>

          {/* Frame Stats Card */}
          <div className="stat-card">
            <div className="stat-card-header">
              <span className="stat-icon">🎯</span>
              <h3>{t('statistics.frames.title')}</h3>
            </div>
            <div className="stat-card-body">
              <div className="stat-item-large">
                <span className="stat-value">{games.totalFrames}</span>
                <span className="stat-label">{t('statistics.frames.totalFrames')}</span>
              </div>
              <div className="stat-row">
                <div className="stat-item">
                  <span className="stat-value stat-positive">{games.framesWon}</span>
                  <span className="stat-label">{t('statistics.frames.won')}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value stat-negative">{games.framesLost}</span>
                  <span className="stat-label">{t('statistics.frames.lost')}</span>
                </div>
              </div>
              <div className="stat-item-highlight">
                <span className="stat-value-big">{games.frameWinRate.toFixed(1)}%</span>
                <span className="stat-label">{t('statistics.frames.winRate')}</span>
              </div>
            </div>
          </div>

          {/* Break Stats Card */}
          <div className="stat-card stat-card-featured">
            <div className="stat-card-header">
              <span className="stat-icon">🏆</span>
              <h3>{t('statistics.breaks.title')}</h3>
            </div>
            <div className="stat-card-body">
              <div className="stat-item-hero">
                <span className="stat-value-hero">{breaks.highestBreak}</span>
                <span className="stat-label">{t('statistics.breaks.highestBreak')}</span>
              </div>
              <div className="stat-row">
                <div className="stat-item">
                  <span className="stat-value stat-highlight">{breaks.centuryBreaks}</span>
                  <span className="stat-label">{t('statistics.breaks.century')}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{breaks.fiftyPlusBreaks}</span>
                  <span className="stat-label">{t('statistics.breaks.fiftyPlus')}</span>
                </div>
              </div>
              <div className="stat-row">
                <div className="stat-item">
                  <span className="stat-value">{breaks.totalBreaks}</span>
                  <span className="stat-label">{t('statistics.breaks.totalBreaks')}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{breaks.averageBreak.toFixed(1)}</span>
                  <span className="stat-label">{t('statistics.breaks.averageBreak')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Scoring Stats Card */}
          <div className="stat-card">
            <div className="stat-card-header">
              <span className="stat-icon">📊</span>
              <h3>{t('statistics.scoring.title')}</h3>
            </div>
            <div className="stat-card-body">
              <div className="stat-item-large">
                <span className="stat-value">{scoring.totalPoints.toLocaleString()}</span>
                <span className="stat-label">{t('statistics.scoring.totalPoints')}</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{scoring.averagePointsPerFrame.toFixed(1)}</span>
                <span className="stat-label">{t('statistics.scoring.avgPerFrame')}</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{scoring.averagePointsPerGame.toFixed(1)}</span>
                <span className="stat-label">{t('statistics.scoring.avgPerGame')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Form */}
      {recentForm && recentForm.formString && (
        <section className="stats-form">
          <h2>{t('statistics.form.title')}</h2>
          <div className="form-display">
            <div className="form-string">
              {recentForm.formString.split('').map((result, index) => (
                <span
                  key={index}
                  className={`form-letter ${result === 'W' ? 'win' : 'loss'}`}
                >
                  {result}
                </span>
              ))}
            </div>
            <div className="form-streak">
              <span className="streak-label">{t('statistics.form.currentStreak')}:</span>
              <span className={`streak-value ${recentForm.currentStreak >= 0 ? 'positive' : 'negative'}`}>
                {Math.abs(recentForm.currentStreak)} {recentForm.currentStreak >= 0 ? 'W' : 'L'}
              </span>
            </div>
          </div>
        </section>
      )}

      {/* Ball Potting Statistics */}
      {ballPotting && (
        <section className="stats-ball-potting">
          <h2>{t('statistics.ballPotting.title')}</h2>
          <div className="ball-stats-grid">
            <div className="ball-stat" data-ball="red">
              <span className="ball-icon">🔴</span>
              <span className="ball-count">{ballPotting.reds}</span>
              <span className="ball-label">{t('statistics.ballPotting.reds')}</span>
            </div>
            <div className="ball-stat" data-ball="yellow">
              <span className="ball-icon">🟡</span>
              <span className="ball-count">{ballPotting.yellows}</span>
              <span className="ball-label">{t('statistics.ballPotting.yellows')}</span>
            </div>
            <div className="ball-stat" data-ball="green">
              <span className="ball-icon">🟢</span>
              <span className="ball-count">{ballPotting.greens}</span>
              <span className="ball-label">{t('statistics.ballPotting.greens')}</span>
            </div>
            <div className="ball-stat" data-ball="brown">
              <span className="ball-icon">🟤</span>
              <span className="ball-count">{ballPotting.browns}</span>
              <span className="ball-label">{t('statistics.ballPotting.browns')}</span>
            </div>
            <div className="ball-stat" data-ball="blue">
              <span className="ball-icon">🔵</span>
              <span className="ball-count">{ballPotting.blues}</span>
              <span className="ball-label">{t('statistics.ballPotting.blues')}</span>
            </div>
            <div className="ball-stat" data-ball="pink">
              <span className="ball-icon">🩷</span>
              <span className="ball-count">{ballPotting.pinks}</span>
              <span className="ball-label">{t('statistics.ballPotting.pinks')}</span>
            </div>
            <div className="ball-stat" data-ball="black">
              <span className="ball-icon">⚫</span>
              <span className="ball-count">{ballPotting.blacks}</span>
              <span className="ball-label">{t('statistics.ballPotting.blacks')}</span>
            </div>
            <div className="ball-stat ball-stat-total">
              <span className="ball-icon">🎱</span>
              <span className="ball-count">{ballPotting.totalBalls}</span>
              <span className="ball-label">{t('statistics.ballPotting.totalBalls')}</span>
            </div>
          </div>
        </section>
      )}

      {/* Charts Section */}
      <section className="stats-charts">
        <h2>{t('statistics.charts.title', 'Performance Charts')}</h2>
        
        <div className="charts-grid">
          {/* Game Win Rate Chart */}
          {games.totalGames > 0 && (
            <div className="chart-card">
              <h3>{t('statistics.charts.gameWinRate', 'Game Win Rate')}</h3>
              <WinRateChart stats={games} />
            </div>
          )}

          {/* Frame Win Rate Chart */}
          {games.totalFrames > 0 && (
            <div className="chart-card">
              <h3>{t('statistics.charts.frameWinRate', 'Frame Win Rate')}</h3>
              <FrameWinRateChart stats={games} />
            </div>
          )}

          {/* Break Distribution Chart */}
          {breakDistribution && (
            <div className="chart-card chart-card-wide">
              <h3>{t('statistics.breakDistribution.title')}</h3>
              <BreakDistributionChart distribution={breakDistribution} />
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Statistics;

